import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, MapPin, Clock, CreditCard, ArrowLeft, CheckCircle, Copy, Crown, ShieldCheck, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { API, toast } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDeliveryStatusLabel } from '@/utils/displayLabels';
import { UI_ACTION_LABELS, UI_STATUS_LABELS } from '@/constants/uiLabels';
import MobileMoneyPayment from '@/components/shared/MobileMoneyPayment';
import DeliveryLabelDialog from '@/components/shared/DeliveryLabelDialog';
import DeliveryEstimateSummary from '@/components/shared/DeliveryEstimateSummary';
import { MOBILE_MONEY_METHOD_IDS } from '@/utils/paymentMethodAvailability';
import { uploadPaymentProof } from '@/utils/paymentProofUpload';
import { getDeliveryLabelCapabilities } from '@/utils/deliveryLabels';
import { getPricingSummary, getPromoFeedbackMessage, normalizePromoCodeInput } from '@/utils/promoCodes';

const resolveWalletPaymentMessage = (payload, fallbackMessage) => {
  const candidate = typeof payload?.message === 'string' ? payload.message.trim() : '';
  return candidate || fallbackMessage;
};

const isAlreadyProcessedPayment = (payload) => Boolean(payload?.already_processed || payload?.idempotent_replay);

const DeliveryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [walletSummary, setWalletSummary] = useState(null);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoApplying, setPromoApplying] = useState(false);

  useEffect(() => {
    loadDelivery();
    loadWalletSummary();
  }, [id]);

  useEffect(() => {
    setPromoCodeInput(delivery?.promo_code || '');
  }, [delivery?.promo_code]);

  const loadDelivery = async ({ silent = false } = {}) => {
    try {
      const response = await axios.get(`${API}/deliveries/${id}`);
      setDelivery(response.data);
    } catch (error) {
      if (!silent) {
        toast.error('Erreur lors du chargement');
        navigate('/vendeur');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadWalletSummary = async () => {
    try {
      const response = await axios.get(`${API}/wallet`);
      setWalletSummary(response.data || null);
    } catch (error) {
      setWalletSummary(null);
    }
  };

  const handlePayment = async () => {
    if (processingPayment) {
      return;
    }

    if (!paymentMethod) {
      toast.error('Sélectionnez une méthode de paiement');
      return;
    }
    if (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && !paymentProofFile) {
      toast.error('Ajoutez une preuve de paiement Mobile Money');
      return;
    }

    setProcessingPayment(true);

    try {
      let proofFileId;
      if (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod)) {
        const uploadedProof = await uploadPaymentProof({
          file: paymentProofFile,
          context: 'delivery_payment',
          deliveryId: delivery.id,
        });
        proofFileId = uploadedProof?.id;
      }
      const paymentResponse = await axios.post(`${API}/payments`, null, {
        params: {
          delivery_id: delivery.id,
          method: paymentMethod,
          transaction_id: transactionId.trim() || undefined,
          proof_file_id: proofFileId,
        }
      });
      const paymentPayload = paymentResponse.data || {};

      const defaultSuccessMessage = paymentMethod === 'wallet'
        ? (isAlreadyProcessedPayment(paymentPayload)
          ? 'Paiement du portefeuille déjà confirmé avec succès.'
          : 'Paiement du portefeuille confirmé avec succès.')
        : 'Preuve de paiement envoyee. Validation admin en attente.';

      toast.success(
        resolveWalletPaymentMessage(
          paymentPayload,
          defaultSuccessMessage
        )
      );
      setShowPaymentDialog(false);
      setPaymentMethod('');
      setTransactionId('');
      setPaymentProofFile(null);
      await Promise.all([loadDelivery({ silent: true }), loadWalletSummary()]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'INSUFFICIENT_BALANCE') {
        toast.error('Solde du portefeuille insuffisant pour payer cette livraison');
      } else if (detail === 'PAYMENT_ALREADY_PROCESSING') {
        toast.error('Le paiement du portefeuille est déjà en cours de finalisation. Actualisez la livraison avant de recommencer.');
      } else if (detail === 'Delivery already paid') {
        toast.success('Cette livraison est déjà payée.');
        setShowPaymentDialog(false);
        await Promise.all([loadDelivery({ silent: true }), loadWalletSummary()]);
      } else {
        toast.error(detail || 'Erreur lors du paiement');
      }
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleDuplicateDelivery = async () => {
    if (duplicating) {
      return;
    }

    setDuplicating(true);
    try {
      const response = await axios.post(`${API}/deliveries/${delivery.id}/duplicate`);
      const payload = response.data?.data || response.data;
      toast.success(response.data?.message || 'Commande dupliquee. Verifiez le nouveau client avant validation.');
      navigate('/vendeur/create', {
        state: {
          deliveryDuplicate: payload,
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de dupliquer cette commande');
    } finally {
      setDuplicating(false);
    }
  };

  const handleApplyPromoCode = async () => {
    const normalizedPromoCode = normalizePromoCodeInput(promoCodeInput);
    if (!normalizedPromoCode) {
      toast.error('Saisissez un code promo.');
      return;
    }
    setPromoApplying(true);
    try {
      const response = await axios.post(`${API}/deliveries/${delivery.id}/promo-code`, {
        code: normalizedPromoCode,
      });
      const payload = response.data || {};
      if (!payload.valid && !payload.data?.delivery) {
        toast.error(getPromoFeedbackMessage(payload.status, payload.message));
        return;
      }
      const refreshedDelivery = payload.data?.delivery || delivery;
      setDelivery(refreshedDelivery);
      setPromoCodeInput(refreshedDelivery?.promo_code || normalizedPromoCode);
      toast.success(payload.message || 'Code promo applique a la livraison.');
      await loadWalletSummary();
    } catch (error) {
      toast.error(getPromoFeedbackMessage(error.response?.data?.detail));
    } finally {
      setPromoApplying(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending_payment': { label: 'En attente paiement', class: 'bg-amber-100 text-amber-700' },
      'paid': { label: UI_STATUS_LABELS.paid, class: 'bg-blue-100 text-blue-700' },
      'pickup_in_progress': { label: 'Ramassage en cours', class: 'bg-indigo-100 text-indigo-700' },
      'picked_up': { label: 'Colis récupéré', class: 'bg-purple-100 text-purple-700' },
      'in_transit': { label: 'En transit', class: 'bg-cyan-100 text-cyan-700' },
      'at_relay': { label: 'Au Point de Relais', class: 'bg-orange-100 text-orange-700' },
      'out_for_delivery': { label: 'En livraison', class: 'bg-green-100 text-green-700' },
      'delivered': { label: UI_STATUS_LABELS.delivered, class: 'bg-green-100 text-green-700' },
      'cancelled': { label: 'Annulé', class: 'bg-red-100 text-red-700' }
    };
    const info = statusMap[status] || { label: formatDeliveryStatusLabel(status), class: 'bg-gray-100 text-gray-700' };
    return <Badge className={info.class}>{info.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!delivery) {
    return null;
  }

  const serviceTier = delivery.service_tier || (delivery.is_express ? 'express' : 'standard');
  const isExpressTier = serviceTier === 'express';
  const isInsured = Boolean(delivery.is_insured);
  const transportAmount = Number(delivery.transport_amount ?? delivery.cost ?? 0);
  const expressSurchargeAmount = Number(delivery.express_surcharge_amount ?? 0);
  const insuranceAmount = Number(delivery.insurance_amount ?? 0);
  const pricingSummary = getPricingSummary(delivery, {
    fallbackAmount: delivery.cost,
    fallbackCurrency: delivery.currency,
  });
  const totalAmount = Number(pricingSummary.amountAfter ?? delivery.cost ?? 0);
  const labelCapabilities = getDeliveryLabelCapabilities(delivery);

  return (
    <div className="max-w-4xl mx-auto">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate('/vendeur')}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {UI_ACTION_LABELS.back}
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl">Livraison #{delivery.id.slice(0, 8)}</CardTitle>
                  <CardDescription>Créée le {new Date(delivery.created_at).toLocaleDateString('fr-FR')}</CardDescription>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {!isExpressTier && <Badge variant="outline">Service Standard</Badge>}
                    {isExpressTier && (
                      <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        EXPRESS
                      </Badge>
                    )}
                    {isInsured && (
                      <Badge className="bg-cyan-600 text-white flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" />
                        Assurance
                      </Badge>
                    )}
                  </div>
                </div>
                {getStatusBadge(delivery.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4 text-red-600" />
                  Client final
                </h3>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium">{delivery.client_info.full_name}</p>
                  <p className="text-sm text-gray-600">{delivery.client_info.phone}</p>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="font-semibold mb-3">Itinéraire</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">Ramassage</p>
                      <p className="text-sm text-gray-600">{delivery.pickup_address}</p>
                      {delivery.pickup_reference && (
                        <p className="text-sm text-gray-500">Référence : {delivery.pickup_reference}</p>
                      )}
                      <p className="text-sm text-gray-500">{delivery.pickup_district}, {delivery.pickup_city}</p>
                    </div>
                  </div>

                  <div className="ml-4 border-l-2 border-dashed border-gray-300 h-6" />

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Livraison</p>
                      <p className="text-sm text-gray-600">{delivery.delivery_address}</p>
                      {delivery.delivery_reference && (
                        <p className="text-sm text-gray-500">Référence : {delivery.delivery_reference}</p>
                      )}
                      <p className="text-sm text-gray-500">{delivery.delivery_district}, {delivery.delivery_city}</p>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Poids</p>
                  <p className="font-medium">{delivery.weight_verified || delivery.weight} kg</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Distance</p>
                  <p className="font-medium">{delivery.distance_km?.toFixed(1) || 'N/A'} km</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Livreur</p>
                  <p className="font-medium">{delivery.livreur_name || 'Non assigné'}</p>
                </div>
              </div>

              <div className="mt-4">
                <DeliveryEstimateSummary
                  source={delivery}
                  testId="delivery-details-estimate"
                />
              </div>

              <Separator />

              <div>
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-sm">{delivery.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Tracking History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-600" />
                Historique de suivi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {delivery.tracking_history.map((entry, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full" />
                      {index < delivery.tracking_history.length - 1 && (
                        <div className="w-0.5 h-full bg-gray-300 my-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="font-medium text-sm">{entry.note}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString('fr-FR')}
                      </p>
                      {entry.updated_by && (
                        <p className="text-xs text-gray-600">Par: {entry.updated_by}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-2 border-red-200">
            <CardHeader>
              <CardTitle className="text-lg">Coût de livraison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-4xl font-bold gradient-text">{totalAmount.toLocaleString()} {delivery.currency}</p>
              </div>

                <div className="mt-4 rounded-lg border border-gray-200 p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Transport</span>
                    <span>{transportAmount.toLocaleString()} {delivery.currency}</span>
                  </div>
                <div className="flex items-center justify-between">
                  <span>Supplément Express</span>
                  <span>+ {expressSurchargeAmount.toLocaleString()} {delivery.currency}</span>
                </div>
                  <div className="flex items-center justify-between">
                    <span>Supplément Assurance</span>
                    <span>+ {insuranceAmount.toLocaleString()} {delivery.currency}</span>
                  </div>
                  {pricingSummary.hasDiscount && (
                    <>
                      <div className="flex items-center justify-between">
                        <span>Sous-total brut</span>
                        <span>{pricingSummary.amountBefore.toLocaleString()} {pricingSummary.currency}</span>
                      </div>
                      <div className="flex items-center justify-between text-green-700">
                        <span>Code promo{pricingSummary.promoCode ? ` (${pricingSummary.promoCode})` : ''}</span>
                        <span>- {pricingSummary.discountAmount.toLocaleString()} {pricingSummary.currency}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t pt-1 flex items-center justify-between font-semibold">
                    <span>Total</span>
                    <span>{totalAmount.toLocaleString()} {pricingSummary.currency}</span>
                  </div>
                </div>

               {delivery.status === 'pending_payment' && (
                 <div className="mt-4 rounded-lg border border-dashed border-gray-300 p-3 text-sm">
                   <Label htmlFor="delivery-details-promo" className="text-sm font-medium">Code promo</Label>
                   <div className="mt-2 flex gap-2">
                     <Input
                       id="delivery-details-promo"
                       data-testid="delivery-details-promo-input"
                       value={promoCodeInput}
                       onChange={(event) => setPromoCodeInput(event.target.value.toUpperCase())}
                       placeholder="PROMO20"
                     />
                     <Button
                       type="button"
                       data-testid="apply-delivery-details-promo-btn"
                       variant="outline"
                       onClick={handleApplyPromoCode}
                       disabled={promoApplying}
                     >
                       {promoApplying ? 'Application...' : 'Appliquer'}
                     </Button>
                   </div>
                   <p className="mt-2 text-xs text-gray-500">
                     Le backend recalcule le montant net attendu avant toute validation Mobile Money.
                   </p>
                 </div>
               )}
               
               {delivery.status === 'pending_payment' && (
                 <div className="mt-6">
                  <Button
                    data-testid="pay-now-btn"
                    className="w-full bg-red-500 hover:bg-red-600"
                    onClick={() => setShowPaymentDialog(true)}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {UI_ACTION_LABELS.payNow}
                  </Button>
                </div>
              )}
              
              {delivery.status !== 'pending_payment' && (
                <div className="mt-4 p-3 bg-green-50 rounded flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">{UI_STATUS_LABELS.paymentConfirmed}</span>
                </div>
              )}

              <div className="mt-4">
                <Button
                  type="button"
                  data-testid="duplicate-delivery-btn"
                  variant="outline"
                  className="w-full"
                  onClick={handleDuplicateDelivery}
                  disabled={duplicating}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {duplicating ? 'Preparation...' : 'Dupliquer'}
                </Button>
              </div>

              <div className="mt-4">
                <Button
                  type="button"
                  data-testid="open-label-dialog-btn"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowLabelDialog(true)}
                  disabled={!labelCapabilities.available}
                >
                  <ScanLine className="w-4 h-4 mr-2" />
                  Étiquette
                </Button>
                <p className="mt-2 text-xs text-gray-500">
                  {labelCapabilities.available
                    ? "Aperçu, téléchargement et impression du PDF d'étiquette."
                    : labelCapabilities.reason}
                </p>
              </div>
            </CardContent>
          </Card>

          {delivery.relay_points && delivery.relay_points.length > 0 && (
            <Card className="bg-cyan-50 border-cyan-200">
              <CardHeader>
                <CardTitle className="text-sm">Livraison inter-villes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  Ce colis passera par {delivery.relay_points.length} Point(s) de Relais
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
            <DialogHeader>
              <DialogTitle>Paiement de la livraison</DialogTitle>
              <DialogDescription>
                Montant: {pricingSummary.amountAfter.toLocaleString()} {pricingSummary.currency}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span>Montant brut</span>
                  <span>{pricingSummary.amountBefore.toLocaleString()} {pricingSummary.currency}</span>
                </div>
                {pricingSummary.hasDiscount && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>Code promo{pricingSummary.promoCode ? ` (${pricingSummary.promoCode})` : ''}</span>
                    <span>- {pricingSummary.discountAmount.toLocaleString()} {pricingSummary.currency}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-2 font-semibold">
                  <span>Net attendu</span>
                  <span>{pricingSummary.amountAfter.toLocaleString()} {pricingSummary.currency}</span>
                </div>
              </div>

              <div>
                <Label>Méthode de paiement</Label>
                <Select value={paymentMethod} onValueChange={(value) => {
                setPaymentMethod(value);
                if (value === 'wallet') {
                  setTransactionId('');
                  setPaymentProofFile(null);
                }
              }}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  {delivery.currency === 'FCFA' ? (
                    <>
                      <SelectItem value="mtn_money">MTN Mobile Money</SelectItem>
                      <SelectItem value="airtel_money">Airtel Money</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="mpesa">M-Pesa</SelectItem>
                      <SelectItem value="orange_money">Orange Money</SelectItem>
                    </>
                  )}
                  <SelectItem value="wallet">Portefeuille</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && (
              <MobileMoneyPayment
                amount={pricingSummary.amountAfter}
                currency={pricingSummary.currency}
                selectedMethod={paymentMethod}
                transactionId={transactionId}
                receiptFile={paymentProofFile}
                onTransactionIdChange={setTransactionId}
                onReceiptFileChange={setPaymentProofFile}
                onPaymentMethodSelect={(method) => {
                  setPaymentMethod(method);
                  setTransactionId('');
                  setPaymentProofFile(null);
                }}
              />
            )}

            {paymentMethod === 'wallet' && (
              <div className="p-3 bg-cyan-50 border border-cyan-200 rounded text-sm text-cyan-800">
                Paiement immédiat via le portefeuille.
                <div className="mt-1 font-semibold">
                  Solde actuel: {Number(walletSummary?.balance || 0).toLocaleString()} {walletSummary?.currency || pricingSummary.currency}
                </div>
              </div>
            )}

          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
              {UI_ACTION_LABELS.cancel}
            </Button>
            <Button
              type="button"
              data-testid="confirm-payment-btn"
              className="bg-red-500 hover:bg-red-600"
              onClick={handlePayment}
              disabled={processingPayment || !paymentMethod || (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && !paymentProofFile)}
            >
              {processingPayment ? 'Traitement...' : 'Confirmer le paiement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeliveryLabelDialog
        open={showLabelDialog}
        onOpenChange={setShowLabelDialog}
        delivery={delivery}
        allowPreferredFormat
      />
    </div>
  );
};

export default DeliveryDetails;

